import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Tag } from "@carbon/react";
import { Add, SendAlt } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
  PAGE_SIZES,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import {
  useResendSupplierInvitation,
  useResendSupplierInvitations,
  useSupplierInvitationResendPreview,
  useSuppliersByBuyer,
} from "../../hooks/useSuppliers";
import useDebounce from "../../hooks/useDebounce";
import { NetworkError } from "../../components";
import { useNotification, useUser } from "../../context";
import {
  SupplierRegistrationResendResult,
  SupplierResponse,
} from "../../types";
import {
  ABILITIES,
  SUPPLIER_STATUS,
  USER_TYPES,
  formatDate,
  formatDateTime,
} from "../../utils";
import "./suppliers.scss";

interface SupplierRow extends DataTableRow {
  id: string;
  name: ReactNode;
  tax: string;
  email: string;
  status: ReactNode;
  actions: ReactNode;
  label: string;
  pendingRegistration: boolean;
}

/** The backend lets a company re-send to a supplier once per this long; the button mirrors it. */
const RESEND_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const isPendingRegistration = (supplier: SupplierResponse) =>
  supplier.userStatus === USER_TYPES.PENDING_REGISTRATION;

const resentWithinCooldown = (supplier: SupplierResponse) =>
  Boolean(supplier.registrationInvitationResentAt) &&
  Date.now() - Date.parse(supplier.registrationInvitationResentAt as string) <
    RESEND_COOLDOWN_MS;

/**
 * Rebuilt on the Carbon primitives layer (Task 4, Suppliers flow) on `PageHeader`/`DataTable`/
 * `EmptyState`, following `containers/Sites/Sites.tsx`/`containers/Users/Users.tsx` as the
 * reviewed reference — same server-side search + pagination shape, same 0↔1-indexed `Pagination`
 * boundary. `GET /suppliers/by-buyer` (`useSuppliersByBuyer`, unchanged) already accepted
 * `page`/`query`; this task only changes how the result is rendered.
 *
 * Both toolbar CTAs ("+ New Supplier" / "+ Upload Bulk Suppliers") are rendered unconditionally
 * in `PageHeader`'s action slot, not gated behind `isTrulyEmpty` the way Sites'/Users' single
 * `addAction` is — the pre-Carbon screen showed both buttons regardless of whether the list had
 * any rows, and that is preserved exactly here.
 *
 * The old table's trailing chevron-link column is dropped: the company-name cell is already a
 * real `Link` to `/suppliers/:id`, which is the row-action the brief calls a sufficient
 * "clear semantic link" on its own — a second link to the same destination added nothing.
 *
 * `isError` has no equivalent slot on the shared `DataTable` primitive (Sites/Users don't surface
 * one either), so a network error swaps the whole table out for the existing `NetworkError`
 * component, same as the pre-Carbon screen did — only the loading state moves onto `DataTable`'s
 * own Carbon skeleton (`isLoading`) instead of the old free-floating `ClipLoader`.
 */
const Suppliers = () => {
  const { t } = useTranslation();
  const { can } = useUser();
  const { openNotification } = useNotification();
  const canManageSuppliers = can(ABILITIES.MANAGE_SUPPLIERS);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 500);

  // Resend: one row at a time, or a bulk send over the selection (or every pending supplier when
  // nothing is selected) behind a confirmation that counts recipients with a dry run first.
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // A new search always restarts at the first page — same convention Sites.tsx/Users.tsx use.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  // Selection is per page: rows from another page or search aren't on screen to be confirmed.
  useEffect(() => {
    setSelectedIds([]);
  }, [page, pageSize, debouncedSearchTerm]);

  const describeResend = (result: SupplierRegistrationResendResult) => {
    const onlySkip =
      result.sentCount === 0 && result.skippedCount === 1
        ? result.skipped[0]
        : undefined;
    if (onlySkip?.reason === "COOLDOWN") {
      return t("suppliers.resend.cooldown", {
        date: formatDateTime(onlySkip.nextAllowedAt ?? undefined),
      });
    }
    if (onlySkip?.reason === "ALREADY_REGISTERED") {
      return t("suppliers.resend.alreadyRegistered");
    }
    if (onlySkip) {
      return t("suppliers.resend.error");
    }
    return result.skippedCount > 0
      ? t("suppliers.resend.successWithSkipped", {
          count: result.sentCount,
          skipped: result.skippedCount,
        })
      : t("suppliers.resend.success", { count: result.sentCount });
  };

  const { mutate: resendOne } = useResendSupplierInvitation(
    (result) => {
      openNotification(describeResend(result));
      setResendingId(null);
    },
    () => {
      openNotification(t("suppliers.resend.error"));
      setResendingId(null);
    }
  );

  const { mutate: resendMany, isPending: isResendingMany } =
    useResendSupplierInvitations(
      (result) => {
        openNotification(describeResend(result));
        setConfirmOpen(false);
        setSelectedIds([]);
      },
      () => {
        openNotification(t("suppliers.resend.error"));
        setConfirmOpen(false);
      }
    );

  const selectedSupplierIds = useMemo(
    () => selectedIds.map(String),
    [selectedIds]
  );
  const { data: preview, isFetching: isPreviewing } =
    useSupplierInvitationResendPreview(selectedSupplierIds, confirmOpen);

  const {
    data: suppliersPage,
    isLoading,
    isError,
  } = useSuppliersByBuyer(page, debouncedSearchTerm, undefined, pageSize);

  const visibleSuppliers = useMemo(
    () => suppliersPage?.content ?? [],
    [suppliersPage]
  );

  const addActions = (
    <div className="suppliers__header-actions">
      <Button as={Link} to="/suppliers/new" renderIcon={Add}>
        {t("suppliers.massive.add")}
      </Button>
      <Button kind="tertiary" as={Link} to="/suppliers/upload">
        + {t("suppliers.massive.new")}
      </Button>
    </div>
  );

  const rows: SupplierRow[] = visibleSuppliers.map((supplier) => {
    const pendingRegistration = isPendingRegistration(supplier);
    return {
      id: supplier.id,
      label: supplier.commercialName || supplier.companyName || supplier.email,
      pendingRegistration,
      name: (
        <Link to={`/suppliers/${supplier.id}`}>{supplier.commercialName}</Link>
      ),
      tax: supplier.cuit,
      email: supplier.email,
      status: (
        <div className="suppliers__status">
          <Tag
            type={
              supplier.userStatus === USER_TYPES.USER
                ? "green"
                : pendingRegistration
                  ? "purple"
                  : "warm-gray"
            }
            size="sm"
          >
            {
              SUPPLIER_STATUS[
                supplier.userStatus as keyof typeof SUPPLIER_STATUS
              ]
            }
          </Tag>
          {pendingRegistration && supplier.registrationInvitationResentAt ? (
            <span className="suppliers__resent-on">
              {t("suppliers.resend.resentOn", {
                date: formatDate(supplier.registrationInvitationResentAt),
              })}
            </span>
          ) : null}
        </div>
      ),
      actions:
        canManageSuppliers && pendingRegistration ? (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={SendAlt}
            disabled={
              resendingId === supplier.id || resentWithinCooldown(supplier)
            }
            onClick={() => {
              setResendingId(supplier.id);
              resendOne(supplier.id);
            }}
          >
            {t("suppliers.resend.action")}
          </Button>
        ) : null,
    };
  });

  const headers: DataTableColumn<SupplierRow>[] = useMemo(
    () => [
      { key: "name", header: t("register.form.companyName") },
      { key: "tax", header: t("register.form.tax") },
      { key: "email", header: t("register.form.email") },
      { key: "status", header: t("users.status") },
      ...(canManageSuppliers
        ? [
            {
              key: "actions" as const,
              header: t("suppliers.resend.actionsHeader"),
            },
          ]
        : []),
    ],
    [t, canManageSuppliers]
  );

  const bulkResendAction = canManageSuppliers ? (
    <Button
      kind="tertiary"
      renderIcon={SendAlt}
      onClick={() => setConfirmOpen(true)}
    >
      {selectedIds.length > 0
        ? t("suppliers.resend.bulkSelected", { count: selectedIds.length })
        : t("suppliers.resend.bulkAll")}
    </Button>
  ) : null;

  const confirmBody =
    isPreviewing || !preview ? (
      <p>{t("suppliers.resend.confirmLoading")}</p>
    ) : preview.sentCount === 0 ? (
      <p>{t("suppliers.resend.confirmNone")}</p>
    ) : (
      <>
        <p>{t("suppliers.resend.confirmBody", { count: preview.sentCount })}</p>
        {preview.skippedCount > 0 ? (
          <p>
            {t("suppliers.resend.confirmSkipped", {
              count: preview.skippedCount,
            })}
          </p>
        ) : null}
      </>
    );

  const empty = <EmptyState title={t("suppliers.noSuppliersFound")} />;

  return (
    <div>
      <PageHeader title={t("navigation.suppliers")} action={addActions} />

      {isError ? (
        <NetworkError />
      ) : (
        <DataTable
          headers={headers}
          rows={rows}
          isLoading={isLoading}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            label: t("cta.search"),
          }}
          toolbarContent={bulkResendAction}
          selection={
            canManageSuppliers
              ? {
                  selectedIds,
                  onChange: setSelectedIds,
                  getRowLabel: (row) =>
                    t("suppliers.resend.selectRow", { name: row.label }),
                  selectAllLabel: t("suppliers.resend.selectAll"),
                  isRowDisabled: (row) => !row.pendingRegistration,
                  hideDisabledSelection: true,
                }
              : undefined
          }
          pagination={{
            // DataTable's Pagination (Carbon) is 1-indexed; this screen's own paging state — and
            // the paginated-suppliers query it drives — stays 0-indexed to match the API, same
            // boundary conversion Sites.tsx/Users.tsx use.
            page: page + 1,
            pageSize,
            pageSizes: PAGE_SIZES,
            totalItems: suppliersPage?.totalElements ?? 0,
            onPageChange: (newPage, newPageSize) => {
              setPage(newPage - 1);
              setPageSize(newPageSize);
            },
          }}
          empty={empty}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t("suppliers.resend.confirmTitle")}
        body={confirmBody}
        confirmLabel={t("suppliers.resend.confirm")}
        isPending={isResendingMany}
        confirmDisabled={isPreviewing || !preview || preview.sentCount === 0}
        onConfirm={() =>
          resendMany(
            selectedSupplierIds.length > 0 ? selectedSupplierIds : undefined
          )
        }
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default Suppliers;
